import React from 'react';
import {connect} from 'react-redux';
import PropTypes from "prop-types";
import * as uuid from 'uuid';
import ConfigUtils from "qwc2/utils/ConfigUtils";
import MapStateService from "../utils/MapStateService";

const AUTOSAVE_NAME = 'AutoSave';
// Setup runs once per page load, even if the component gets mounted more than once
let setupPromise = null;

/**
 * Keeps a mapstate named "AutoSave" up to date with the current map state.
 * Changes (pan, zoom, layers) only mark the state as dirty; a timer writes it at most
 * once per cooldown. Additionally, the state is written at least once per max interval.
 */
class MapStateAutoSave extends React.Component {
    static propTypes = {
        layers: PropTypes.array,
        map: PropTypes.object
    }

    constructor(props) {
        super(props);
        this.autoSaveId = null;
        this.dirty = false;
        this.inFlight = false;
        this.lastSave = Date.now();
        this.timer = null;
        this.unmounted = false;
    }

    componentDidMount() {
        this.cooldown = ConfigUtils.getConfigProp("mapstateAutosaveCooldown") || 10000;
        this.maxInterval = ConfigUtils.getConfigProp("mapstateAutosaveInterval") || 300000;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.init();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.map.center !== this.props.map.center
            || prevProps.map.zoom !== this.props.map.zoom
            || prevProps.layers !== this.props.layers) {
            this.dirty = true;
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    init() {
        if (setupPromise === null) {
            setupPromise = this.setup().catch(error => {
                setupPromise = null;
                throw error;
            });
        }
        setupPromise.then(id => {
            this.autoSaveId = id;
            this.lastSave = Date.now();
        }).catch(error => {
            console.log('AutoSave api error', error);
        }).then(() => {
            if (!this.unmounted) {
                this.timer = setInterval(this.tick, this.cooldown);
            }
        });
    }

    // Resolves with the id of the AutoSave mapstate, creating it if necessary.
    // Duplicates (e.g. from a former race) are removed, the default one is kept.
    setup() {
        return MapStateService.list().then(list => {
            const hasDefault = list.some(ms => MapStateService.isDefault(ms));
            return MapStateService.removeDuplicates(list, AUTOSAVE_NAME).then(existing => {
                if (existing) {
                    return existing.id;
                }
                const id = uuid.v4();
                const data = {id: id, name: AUTOSAVE_NAME, ...this.currentState()};
                return MapStateService.create(data).then(() => {
                    return hasDefault ? id : MapStateService.setDefault(id, 1).then(() => id);
                }).then(createdId => {
                    // Another window may have created the mapstate at the same time, then both decide for
                    // the same one of them (see removeDuplicates)
                    return MapStateService.list()
                        .then(newList => MapStateService.removeDuplicates(newList, AUTOSAVE_NAME))
                        .then(keep => keep ? keep.id : createdId)
                        .catch(() => createdId);
                });
            });
        });
    }

    currentState() {
        return MapStateService.buildState(this.props.map, this.props.layers);
    }

    onVisibilityChange = () => {
        if (document.visibilityState === 'hidden' && this.dirty) {
            this.save();
        }
    }

    tick = () => {
        if (this.dirty || Date.now() - this.lastSave >= this.maxInterval) {
            this.save();
        }
    }

    save() {
        if (this.autoSaveId === null || this.inFlight) {
            return;
        }
        this.inFlight = true;
        this.dirty = false;
        MapStateService.update(this.autoSaveId, this.currentState()).then(() => {
            this.lastSave = Date.now();
        }).catch(error => {
            console.log('AutoSave api error', error);
            this.dirty = true;
        }).then(() => {
            this.inFlight = false;
        });
    }

    render() {
        return null;
    }
}

export default connect(state => ({
    layers: state.layers.flat,
    map: state.map
}))(MapStateAutoSave);
